import React, { useEffect } from 'react';
import { Routes, Route } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';

// Layout
import Navbar from './components/Layout/Navbar';
import Footer from './components/Layout/Footer';

// Pages
import Home from './pages/Home';
import Products from './pages/Products';
import Categories from './pages/Categories';
import ProductDetail from './pages/ProductDetail';
import Cart from './pages/Cart';
import Checkout from './pages/Checkout';
import Login from './pages/Auth/Login';
import Register from './pages/Auth/Register';
import ForgotPassword from './pages/Auth/ForgotPassword';
import Profile from './pages/Profile';
import MyAccount from './pages/MyAccount';
import Orders from './pages/Orders';
import OrderDetail from './pages/OrderDetail';
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentFailure from './pages/PaymentFailure';
import OrderConfirmation from './pages/OrderConfirmation';
// Info Pages
import AboutUs from './pages/Info/AboutUs';
import Contact from './pages/Info/Contact';
import FAQ from './pages/Info/FAQ';
import ShippingPolicy from './pages/Info/ShippingPolicy';
import ReturnsExchangePolicy from './pages/Info/ReturnsExchangePolicy';
import PrivacyPolicy from './pages/Info/PrivacyPolicy';
import TermsOfService from './pages/Info/TermsOfService';

// Service Pages
import ThreeDPrinting from './pages/Services/ThreeDPrinting';
import ThreeDDesignPrototyping from './pages/Services/ThreeDDesignPrototyping';

// Quote Pages
import MyQuotes from './pages/MyQuotes';

// Admin Pages
import AdminDashboard from './pages/Admin/Dashboard';
import AdminProducts from './pages/Admin/Products';
import AdminOrders from './pages/Admin/Orders';
import AdminUsers from './pages/Admin/Users';
import AdminCategories from './pages/Admin/Categories';
import AdminCoupons from './pages/Admin/Coupons';
import AdminShipping from './pages/Admin/Shipping';
import AdminMessages from './pages/Admin/Messages';
import AdminAvailableColors from './pages/Admin/AvailableColors';
import AdminQuotes from './components/Admin/AdminQuotes';
import AdminLayout from './components/Layout/AdminLayout';

// Components
import ProtectedRoute from './components/Auth/ProtectedRoute';
import AdminRoute from './components/Auth/AdminRoute';
import LoadingSpinner from './components/UI/LoadingSpinner';
import AdminSetup from './components/AdminSetup';
import { initFontOptimization } from './utils/fontOptimization';
import { initPerformanceOptimizations } from './utils/performanceOptimization';

function App({ ssrData }) {
  const { loading } = useAuth();

  // Initialize font optimization on app load
  useEffect(() => {
    initFontOptimization();
    initPerformanceOptimizations();
  }, []);

  // Store SSR data for components that need it
  useEffect(() => {
    if (ssrData) {
      // Make SSR data available to components
      window.__APP_SSR_DATA__ = ssrData;
    }
  }, [ssrData]);

  if (loading) {
    return <LoadingSpinner fullScreen />;
  }

  return (
    <div className="App">
      <Navbar />
      <main style={{ minHeight: 'calc(100vh - 140px)' }}>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<Home />} />
          <Route path="/products" element={<Products />} />
          <Route path="/categories" element={<Categories />} />
          <Route path="/products/:id" element={<ProductDetail />} />
          <Route path="/cart" element={<Cart />} />
          {/* Informational Pages */}
          <Route path="/about" element={<AboutUs />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/shipping" element={<ShippingPolicy />} />
          <Route path="/returns" element={<ReturnsExchangePolicy />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          
          {/* Service Pages */}
          <Route path="/services/3d-printing" element={<ThreeDPrinting />} />
          <Route path="/services/3d-design-prototyping" element={<ThreeDDesignPrototyping />} />
          
          {/* Auth Routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          
          {/* Development Routes */}
          <Route path="/admin-setup" element={
            <ProtectedRoute>
              <AdminSetup />
            </ProtectedRoute>
          } />
          
          {/* Protected Routes */}
          <Route path="/checkout" element={
            <ProtectedRoute>
              <Checkout />
            </ProtectedRoute>
          } />
          <Route path="/profile" element={
            <ProtectedRoute>
              <MyAccount />
            </ProtectedRoute>
          } />
          <Route path="/orders" element={
            <ProtectedRoute>
              <Orders />
            </ProtectedRoute>
          } />
          <Route path="/orders/:id" element={
            <ProtectedRoute>
              <OrderDetail />
            </ProtectedRoute>
          } />
          <Route path="/my-quotes" element={
            <ProtectedRoute>
              <MyQuotes />
            </ProtectedRoute>
          } />
          <Route path="/payment-success" element={<PaymentSuccess />} />
          <Route path="/payment-failure" element={<PaymentFailure />} />
          <Route path="/order-confirmation/:id" element={
            <ProtectedRoute>
              <OrderConfirmation />
            </ProtectedRoute>
          } />
          
          {/* Admin Routes (nested for persistent layout) */}
          <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
            <Route index element={<AdminDashboard />} />
            <Route path="products" element={<AdminProducts />} />
            <Route path="orders" element={<AdminOrders />} />
            <Route path="quotes" element={<AdminQuotes />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="categories" element={<AdminCategories />} />
            <Route path="colors" element={<AdminAvailableColors />} />
            <Route path="coupons" element={<AdminCoupons />} />
            <Route path="shipping" element={<AdminShipping />} />
            <Route path="messages" element={<AdminMessages />} />
            <Route path="settings" element={<div>Settings coming soon...</div>} />
          </Route>
        </Routes>
      </main>
      <Footer />
    </div>
  );
}

export default App;
